import type { WaypointResponse } from '@/shared/api/routes'
import type { TripRouteScheduleResponse } from '@/shared/api/routes'

/**
 * Merges waypoints from multiple sources (trip-level and route-level) into a single array.
 * 
 * Strategy:
 * 1. Start with trip-level waypoints (route_id IS NULL) - these have trip-specific time fields
 * 2. Merge in waypoints from routes, preserving trip-specific time fields and order_index
 * 3. Deduplicate by ID, with trip-level waypoints taking precedence for time fields
 * 
 * @param tripWaypoints - Waypoints from trip level (route_id IS NULL)
 * @param routeSchedule - Route schedule containing route-level waypoints
 * @returns Merged array of waypoints with deduplication
 */
export function mergeTripWaypoints(
  tripWaypoints: WaypointResponse[],
  routeSchedule: TripRouteScheduleResponse | null
): WaypointResponse[] {
  const waypointMap = new Map<string, WaypointResponse>()
  
  // First, add trip-level waypoints (route_id IS NULL)
  // These have time fields from trip_waypoints junction table
  tripWaypoints.forEach(wp => {
    if (wp.id) {
      waypointMap.set(wp.id, wp)
    }
  })
  
  // Then, merge waypoints from routes
  // These may be the same waypoints but with more complete route data
  if (routeSchedule) {
    routeSchedule.days.forEach(day => {
      day.routes.forEach(routeEntry => {
        if (routeEntry.waypoints && routeEntry.waypoints.length > 0) {
          routeEntry.waypoints.forEach(wp => {
            if (wp.id) {
              const existing = waypointMap.get(wp.id)
              if (!existing) {
                // New waypoint from route, add it
                waypointMap.set(wp.id, wp)
              } else {
                // Merge route waypoint data with existing trip waypoint
                // CRITICAL: Always preserve time fields, order_index, and trip_day from existing trip waypoint
                // (these come from trip_waypoints junction table via GET /routes/waypoints)
                // Trip waypoint is the source of truth for trip-specific metadata
                waypointMap.set(wp.id, {
                  ...wp,
                  ...existing,
                  // CRITICAL: Always use trip waypoint time fields first (they're the source of truth)
                  // Only fallback to route waypoint if trip waypoint doesn't have them
                  // This ensures updates to trip waypoint times are preserved
                  start_time: existing.start_time !== undefined && existing.start_time !== null 
                    ? existing.start_time 
                    : (wp.start_time ?? undefined),
                  end_time: existing.end_time !== undefined && existing.end_time !== null 
                    ? existing.end_time 
                    : (wp.end_time ?? undefined),
                  // Preserve order_index from trip waypoint to maintain position
                  order_index: existing.order_index ?? wp.order_index ?? undefined,
                  // CRITICAL: Always preserve trip_day from trip waypoint (source of truth)
                  // This includes null values (null means "not assigned to a day")
                  // trip_day from route schedule waypoints should now also be correct after backend fix,
                  // but trip waypoints remain the authoritative source
                  // Use trip waypoint's trip_day if it exists (including null), otherwise fallback to route schedule
                  trip_day: existing.trip_day !== undefined ? existing.trip_day : wp.trip_day
                })
              }
            }
          })
        }
      })
    })
  }
  
  const merged = Array.from(waypointMap.values())
  return merged
}

